<template>
  <div v-if="isVisible" class="overlay">
    <div class="overlay-backdrop" @click="close"></div>
    <div class="overlay-content">
      <div class="overlay-header">
        <h2>Start Route: {{ route?.route_name || route?.routeName || '' }}</h2>
        <button class="close-button" @click="close">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
               viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div class="form-container">
        <!-- Gateway Selection -->
        <div class="form-group">
          <div class="label-container">
            <label class="form-label">Select Gateway Device</label>
            <div class="hint-icon" title="Choose a reading device gateway to collect meter readings">?</div>
          </div>

          <!-- Search/Filter for gateways -->
          <div class="search-input-container">
            <input
                type="text"
                v-model="gatewaySearch"
                class="search-input"
                placeholder="Search gateway by ID or type..."
                @input="filterGateways"
            />
          </div>

          <!-- Gateway List -->
          <div class="gateway-list-container">
            <div v-if="loadingGateways" class="loading-state">
              <div class="loading-spinner"></div>
              <span>Loading gateways...</span>
            </div>

            <div v-else-if="filteredGateways.length === 0" class="empty-state">
              <p>No gateway devices found</p>
            </div>

            <div v-else class="gateway-list">
              <div
                  v-for="gateway in filteredGateways"
                  :key="gateway.identification_number"
                  :class="['gateway-item', { selected: selectedGateway?.identification_number === gateway.identification_number }]"
                  @click="selectGateway(gateway)"
              >
                <div class="gateway-radio">
                  <input
                      type="radio"
                      :id="`gateway-${gateway.identification_number}`"
                      :value="gateway.identification_number"
                      v-model="selectedGatewayId"
                  />
                  <label :for="`gateway-${gateway.identification_number}`"></label>
                </div>
                <div class="gateway-info">
                  <div class="gateway-id">{{ gateway.identification_number }}</div>
                  <div class="gateway-type">{{ gateway.type }}</div>
                  <div class="gateway-created">Created: {{ formatDate(gateway.created_at) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Driver Assignment - UPDATED -->
        <div class="form-group">
          <div class="label-container">
            <label class="form-label">Assign Driver</label>
            <div class="hint-icon" title="Select a driver from your organization to assign to this route">?</div>
          </div>

          <!-- Search/Filter for drivers -->
          <div class="search-input-container">
            <input
                type="text"
                v-model="driverSearch"
                class="search-input"
                placeholder="Search driver by name or email..."
                @input="filterDrivers"
            />
          </div>

          <!-- Driver List -->
          <div class="gateway-list-container">
            <div v-if="loadingDrivers" class="loading-state">
              <div class="loading-spinner"></div>
              <span>Loading drivers...</span>
            </div>

            <div v-else-if="filteredDrivers.length === 0" class="empty-state">
              <p>No drivers found</p>
            </div>

            <div v-else class="gateway-list">
              <div
                  v-for="driver in filteredDrivers"
                  :key="driver.user_id"
                  :class="['gateway-item', { selected: selectedDriver?.user_id === driver.user_id }]"
                  @click="selectDriver(driver)"
              >
                <div class="gateway-radio">
                  <input
                      type="radio"
                      :id="`driver-${driver.user_id}`"
                      :value="driver.user_id"
                      v-model="selectedDriverId"
                  />
                  <label :for="`driver-${driver.user_id}`"></label>
                </div>
                <div class="gateway-info">
                  <div class="gateway-id">{{ getDriverDisplayName(driver) }}</div>
                  <div class="gateway-type">{{ driver.email }}</div>
                  <div class="gateway-created" v-if="driver.role">Role: {{ driver.role }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Route Summary -->
        <div class="route-summary">
          <h3 class="summary-title">Route Summary</h3>
          <div class="summary-content">
            <div class="summary-item">
              <span class="summary-label">Route Name:</span>
              <span class="summary-value">{{ route?.route_name || route?.routeName || '-' }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Number of Meters:</span>
              <span class="summary-value">{{ getNumberOfMeters() }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Current State:</span>
              <span class="summary-value">{{ route?.state || '-' }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Organization:</span>
              <span class="summary-value">{{ getCurrentTenant() }}</span>
            </div>
            <div v-if="selectedGateway" class="summary-item">
              <span class="summary-label">Selected Gateway:</span>
              <span class="summary-value">{{ selectedGateway.identification_number }}</span>
            </div>
            <div v-if="selectedDriver" class="summary-item">
              <span class="summary-label">Assigned Driver:</span>
              <span class="summary-value">{{ selectedDriver.email }}</span>
            </div>
          </div>
        </div>

        <div v-if="submitError" class="submit-error">{{ submitError }}</div>
      </div>

      <div class="form-actions">
        <button class="action-button cancel-button" @click="close">
          Cancel
        </button>
        <button
            class="action-button start-button"
            @click="startRoute"
            :disabled="!canStart || isStarting"
        >
          {{ isStarting ? 'Starting...' : 'Start Route' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StartDriveByOverlay',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    route: {
      type: Object,
      default: null
    },
    mode: {
      type: String,
      default: 'start', // 'start' or 'assign'
      validator: (value) => ['start', 'assign'].includes(value)
    }
  },
  data() {
    return {
      // Gateway selection
      gateways: [],
      filteredGateways: [],
      selectedGateway: null,
      selectedGatewayId: '',
      gatewaySearch: '',
      loadingGateways: false,

      // Driver selection
      drivers: [],
      filteredDrivers: [],
      selectedDriver: null,
      selectedDriverId: '',
      driverSearch: '',
      loadingDrivers: false,

      // Form state
      isStarting: false,
      submitError: ''
    };
  },
  computed: {
    canStart() {
      return this.selectedGateway && this.selectedDriver;
    },
    overlayTitle() {
      return this.mode === 'start' ? 'Start Route' : 'Assign Driver & Gateway';
    },
    submitButtonText() {
      if (this.isStarting) return 'Processing...';
      return this.mode === 'start' ? 'Start Route' : 'Assign';
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden';
        this.resetForm();
        this.fetchGateways();
        this.fetchDrivers();
      } else {
        document.body.style.overflow = '';
      }
    },
    selectedGatewayId(newVal) {
      this.selectedGateway = this.gateways.find(g => g.identification_number === newVal);
    },
    selectedDriverId(newVal) {
      this.selectedDriver = this.drivers.find(d => d.user_id === newVal);
    }
  },
  methods: {
    resetForm() {
      this.selectedGateway = null;
      this.selectedGatewayId = '';
      this.selectedDriver = null;
      this.selectedDriverId = '';
      this.submitError = '';
      this.gatewaySearch = '';
      this.driverSearch = '';
      this.isStarting = false;
    },

    async fetchGateways() {
      this.loadingGateways = true;
      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL || '';
        const response = await fetch(`${apiBaseUrl}/readingdevices`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch gateway devices');
        }

        const data = await response.json();
        this.gateways = Array.isArray(data) ? data : (data.reading_devices || []);
        this.filteredGateways = [...this.gateways];

        if (this.gateways.length === 0) {
          this.submitError = 'No gateway devices found. Please ensure gateway devices are configured for your organization.';
        }
      } catch (error) {
        console.error('Error fetching gateways:', error);
        this.submitError = 'Failed to load gateway devices. Please try again.';
        this.gateways = [];
        this.filteredGateways = [];
      } finally {
        this.loadingGateways = false;
      }
    },

    async fetchDrivers() {
      this.loadingDrivers = true;
      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL || '';
        const response = await fetch(`${apiBaseUrl}/users`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch drivers');
        }

        const data = await response.json();
        // Filter only active users
        this.drivers = (Array.isArray(data) ? data : []).filter(
            user => user.status === 'active' && !user.disabled
        );
        this.filteredDrivers = [...this.drivers];

        if (this.drivers.length === 0) {
          this.submitError = 'No active drivers found in your organization.';
        }
      } catch (error) {
        console.error('Error fetching drivers:', error);
        this.submitError = 'Failed to load drivers. Please try again.';
        this.drivers = [];
        this.filteredDrivers = [];
      } finally {
        this.loadingDrivers = false;
      }
    },

    filterGateways() {
      const search = this.gatewaySearch.toLowerCase();
      if (!search) {
        this.filteredGateways = [...this.gateways];
        return;
      }

      this.filteredGateways = this.gateways.filter(gateway =>
          gateway.identification_number.toLowerCase().includes(search) ||
          gateway.type.toLowerCase().includes(search)
      );
    },

    filterDrivers() {
      const search = this.driverSearch.toLowerCase();
      if (!search) {
        this.filteredDrivers = [...this.drivers];
        return;
      }

      this.filteredDrivers = this.drivers.filter(driver => {
        const displayName = this.getDriverDisplayName(driver).toLowerCase();
        const email = driver.email.toLowerCase();
        return displayName.includes(search) || email.includes(search);
      });
    },

    selectGateway(gateway) {
      this.selectedGateway = gateway;
      this.selectedGatewayId = gateway.identification_number;
    },

    selectDriver(driver) {
      this.selectedDriver = driver;
      this.selectedDriverId = driver.user_id;
    },

    getDriverDisplayName(driver) {
      if (driver.first_name || driver.last_name) {
        return [driver.first_name, driver.last_name].filter(Boolean).join(' ');
      }
      return driver.email;
    },

    getNumberOfMeters() {
      if (!this.route?.meter_readings) return 0;
      return Object.keys(this.route.meter_readings).length;
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      } catch (error) {
        return 'N/A';
      }
    },

    async startRoute() {
      if (!this.canStart) return;

      this.isStarting = true;
      this.submitError = '';

      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL || '';
        const routeId = this.route.route_name || this.route.routeName;

        if (!routeId) {
          throw new Error('Route name is missing. Cannot start route.');
        }

        const response = await fetch(`${apiBaseUrl}/omsroutes/active/${routeId}/assign`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            gateway_name: this.selectedGateway.identification_number,
            assigned_driver: {
              email: this.selectedDriver.email
            }
          })
        });

        const responseData = await response.json();

        if (!response.ok) {
          let errorMessage = responseData.message || 'Failed to start route';

          if (errorMessage.includes('Reading device with identification number')) {
            errorMessage = `Gateway device "${this.selectedGateway.identification_number}" is not available in your organization. Please select a different gateway.`;
          } else if (errorMessage.includes('User with email')) {
            errorMessage = `Driver with email "${this.selectedDriver.email}" not found or not active in your organization.`;
          } else if (errorMessage.includes('Active OMS route not found')) {
            errorMessage = `Route "${routeId}" was not found. The route may have been deleted or the route name is incorrect.`;
          }

          throw new Error(errorMessage);
        }

        this.$emit('route-started', {
          route: responseData.route || this.route,
          gateway: this.selectedGateway,
          driver: this.selectedDriver
        });

        // Navigate to CollectingReadings for both 'start' and 'assign' modes
        // (since 'assign' on InProgress routes means continue the route)
        this.$router.push({
          name: 'Collecting Readings',
          params: { routeName: routeId }
        });

        this.close();

      } catch (error) {
        console.error('Error starting route:', error);
        this.submitError = error.message || 'Failed to start route. Please try again.';
      } finally {
        this.isStarting = false;
      }
    },

    getCurrentTenant() {
      return localStorage.getItem('tenant_id') || 'Unknown';
    },

    close() {
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
/* Overlay base styles */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(32, 32, 32, 0.1) 0%, rgba(16, 16, 16, 0.95) 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease forwards;
}

.overlay-content {
  position: relative;
  width: 600px;
  max-width: 90%;
  max-height: 85vh;
  border-radius: 8px;
  padding: 32px;
  color: white;
  background-color: rgba(50, 50, 50, 0.2);
  backdrop-filter: blur(16px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s ease forwards;
  z-index: 1001;
  overflow-y: auto;
}

/* Header */
.overlay-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 16px;
}

.overlay-header h2 {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0;
}

.close-button {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}

.close-button:hover {
  color: rgba(255, 255, 255, 0.9);
}

/* Form styles */
.form-container {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.label-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.9;
}

.hint-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: rgba(79, 195, 247, 0.3);
  color: #4fc3f7;
  font-size: 10px;
  font-weight: bold;
  cursor: help;
  transition: background-color 0.2s, color 0.2s;
}

.hint-icon:hover {
  background-color: rgba(79, 195, 247, 0.6);
  color: white;
}

/* Input styles */
.search-input-container {
  padding-left: 25px;
}

.input-container {
  padding-left: 25px;
}

.search-input,
.form-input {
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 0;
  font-size: 14px;
  transition: border-color 0.2s;
}

.search-input:focus,
.form-input:focus {
  outline: none;
  border-bottom-color: rgba(255, 255, 255, 0.6);
}

.search-input::placeholder,
.form-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

/* Gateway list */
.gateway-list-container {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 8px;
  margin-left: 25px;
}

.gateway-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gateway-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.gateway-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.gateway-item.selected {
  background: rgba(79, 195, 247, 0.1);
  border: 1px solid rgba(79, 195, 247, 0.3);
}

.gateway-radio input[type="radio"] {
  display: none;
}

.gateway-radio label {
  display: block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  cursor: pointer;
  position: relative;
}

.gateway-item.selected .gateway-radio label {
  border-color: #4fc3f7;
}

.gateway-item.selected .gateway-radio label::after {
  content: '';
  width: 8px;
  height: 8px;
  background: #4fc3f7;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.gateway-info {
  flex: 1;
}

.gateway-id {
  font-size: 14px;
  font-weight: 600;
  color: #4fc3f7;
}

.gateway-type {
  font-size: 12px;
  opacity: 0.8;
  margin-top: 2px;
}

.gateway-created {
  font-size: 11px;
  opacity: 0.6;
  margin-top: 4px;
}

/* Route summary */
.route-summary {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  padding: 20px;
  margin-left: 25px;
}

.summary-title {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-bottom: 16px;
  opacity: 0.9;
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
}

.summary-label {
  opacity: 0.7;
  font-weight: 500;
}

.summary-value {
  font-weight: 400;
  color: #4fc3f7;
}

/* States */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  gap: 12px;
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-top: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 40px;
  opacity: 0.6;
}

.error-message {
  color: #ff8a80;
  font-size: 12px;
  margin-top: 4px;
}

.submit-error {
  color: #ff8a80;
  text-align: center;
  padding: 12px;
  background: rgba(255, 138, 128, 0.1);
  border-radius: 4px;
  font-size: 13px;
}

/* Actions */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 20px;
  border-radius: 4px;
  transition: all 0.2s;
}

.cancel-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.start-button {
  background-color: rgba(79, 195, 247, 0.2);
  border: 1px solid rgba(79, 195, 247, 0.5);
}

.start-button:hover:not(:disabled) {
  background-color: rgba(79, 195, 247, 0.3);
  border-color: rgba(79, 195, 247, 0.7);
}

.start-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 600px) {
  .overlay-content {
    padding: 24px;
  }

  .gateway-list-container {
    margin-left: 0;
  }

  .route-summary {
    margin-left: 0;
  }

  .search-input-container,
  .input-container {
    padding-left: 0;
  }
}
</style>